import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { finalize, first, Observable } from 'rxjs';
import { TalentinaAskResponse } from 'src/app/contracts/responses/talentina-ask-response';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { TalentinaService } from 'src/app/data/talentina.service';
import { ChatMessage } from 'src/app/shared/models/chat-message';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'talentina',
  templateUrl: './talentina.component.html',
  styleUrls: ['./talentina.component.scss'],
  standalone: false
})
export class TalentinaComponent implements OnInit, AfterViewInit, OnDestroy {
  private formBuilder = inject(UntypedFormBuilder);
  private authService = inject(AuthService);
  private talentinaService = inject(TalentinaService);
  private cdr = inject(ChangeDetectorRef);
  private translate = inject(TranslateService);

  @ViewChild('chatmessages') private chatMessagesContainer!: ElementRef;
  formGroup!: UntypedFormGroup;
  isGenerating: boolean = false;
  isCancelling: boolean = false;
  isStopped: boolean = false;
  thread: string = '';
  messages: ChatMessage[] = [];
  shouldAutoScroll: boolean = true;
  private questionHistory: string[] = [];
  private historyIndex: number = -1;
  private historyDraft: string = '';
  readonly user$: Observable<any> = this.authService.getUser();
  readonly connected$ = this.talentinaService.connected$;
  
  readonly maxQuestionLength = 4000;

  constructor() { }

  ngOnInit(): void {
    this.formGroup = this.buildFormGroup();
    this.talentinaService.connect();
    this.talentinaService.connected$.subscribe((connected) => {
      if (!connected && this.isGenerating) {
        this.replaceLastAssistantMessage(this.translate.instant('TalentinaError'), true);
        this.isGenerating = false;
        this.currentQuestion.enable();
      }
    });
    this.talentinaService.talentinaAnswer.subscribe((answer) => {
      if (this.isStopped) {
        return;
      }

      if (answer && answer.trim().length > 0) {
        this.replaceLastAssistantMessage(answer);
      }
    });
  }

  ngOnDestroy(): void {
    this.talentinaService.disconnect();
  }

  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    this.talentinaService.disconnect();
  }

  ngAfterViewInit(): void {
    this.chatMessagesContainer.nativeElement.addEventListener('scroll', () => {
      const element = this.chatMessagesContainer.nativeElement;
      const threshold = 100;
      const position = element.scrollTop + element.clientHeight;
      const height = element.scrollHeight;

      this.shouldAutoScroll = position > height - threshold;
    });
  }

  get currentQuestion() {
    return this.formGroup.get('currentQuestion') as UntypedFormControl;
  }

  buildFormGroup(): UntypedFormGroup {
    return this.formBuilder.group({
      currentQuestion: [null, [Validators.maxLength(this.maxQuestionLength)]],
    });
  }

  send() {
    const question = this.currentQuestion.value;
    if (!question || question.trim() === '' || this.currentQuestion.invalid) {
      return;
    }

    this.saveQuestionToHistory(question);

    this.isGenerating = true;
    this.isCancelling = false;
    this.isStopped = false;

    this.addQuestionAndLoadingMessage(question);
    this.refreshChatView();

    this.currentQuestion.disable();

    if (!this.talentinaService.isConnected()) {
      this.talentinaService.connect();
    }

    this.talentinaService
      .ask(question, this.thread)
      .pipe(
        first(),
        finalize(() => {
          this.currentQuestion.setValue('');
          this.currentQuestion.enable();
          this.resetHistoryNavigation();
          this.isGenerating = false;
        })
      )
      .subscribe({
        next: (answer: TalentinaAskResponse) => {
          this.thread = answer.Thread;
        },
        error: () => {
          this.replaceLastAssistantMessage(this.translate.instant('TalentinaError'), true);
        }
      });
  }

  stop() {
    if (this.isCancelling) {
      return;
    }

    this.isStopped = true;
    this.isCancelling = true;
    this.talentinaService.cancel().pipe(
      first(),
      finalize(() => {
        this.isCancelling = false;
        this.isGenerating = false;
        this.cdr.detectChanges();
      })
    ).subscribe();

    this.markLastAssistantMessageAsStopped();

    this.currentQuestion.setValue('');
    this.currentQuestion.enable();
    this.resetHistoryNavigation();
    this.refreshChatView();
  }

  onQuestionKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
      return;
    }

    if (this.questionHistory.length === 0) {
      return;
    }

    event.preventDefault();

    if (event.key === 'ArrowUp') {
      if (this.historyIndex === -1) {
        this.historyDraft = this.currentQuestion.value ?? '';
        this.historyIndex = this.questionHistory.length - 1;
      } else if (this.historyIndex > 0) {
        this.historyIndex -= 1;
      }

      this.currentQuestion.setValue(this.questionHistory[this.historyIndex]);
      return;
    }

    if (this.historyIndex === -1) {
      return;
    }

    if (this.historyIndex < this.questionHistory.length - 1) {
      this.historyIndex += 1;
      this.currentQuestion.setValue(this.questionHistory[this.historyIndex]);
      return;
    }

    this.currentQuestion.setValue(this.historyDraft);
    this.resetHistoryNavigation();
  }

  private resetHistoryNavigation(): void {
    this.historyIndex = -1;
    this.historyDraft = '';
  }

  private saveQuestionToHistory(question: string): void {
    this.questionHistory.push(question);
    this.resetHistoryNavigation();
  }

  private addQuestionAndLoadingMessage(question: string): void {
    this.messages.push({
      Date: new Date(),
      IsFromSender: true,
      Text: question,
    });

    this.messages.push({
      Date: new Date(),
      IsFromSender: false,
      Text: '',
      IsLoading: true,
    });
  }

  private replaceLastAssistantMessage(text: string, isError: boolean = false): void {
    this.messages.pop();
    this.messages.push({
      Date: new Date(),
      IsFromSender: false,
      Text: text,
      IsLoading: false,
      IsError: isError,
    });
    this.refreshChatView();
  }

  private markLastAssistantMessageAsStopped(): void {
    const lastMessage = this.messages[this.messages.length - 1];
    if (!lastMessage || lastMessage.IsFromSender) {
      return;
    }

    lastMessage.IsLoading = false;
    const stoppedLabel = this.translate.instant('TalentinaStopped');
    if (!lastMessage.Text || lastMessage.Text.trim() === '') {
      lastMessage.Text = stoppedLabel;
      return;
    }

    lastMessage.Text += `\n\n${stoppedLabel}`;
  }

  private refreshChatView(): void {
    this.cdr.detectChanges();
    this.scrollIfNeeded();
  }

  private scrollIfNeeded() {
    if (this.shouldAutoScroll) {
      setTimeout(() => {
        const el = this.chatMessagesContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      });
    }
  }
}
